import { useTranslation } from "react-i18next";
import { CURRENCIES, formatPrice, type PriceMap } from "@nh/shared";

export interface ProductPricesProps {
  prices: PriceMap;
  compareAtPrices?: PriceMap | undefined;
}

/**
 * El precio en las dos monedas.
 *
 * Se enseñan las DOS y no "la primera que tenga valor": el panel existe
 * para detectar justo lo que esa optimización esconde —un producto que
 * se quedó sin precio en dólares y que la tienda en inglés no puede
 * vender—. Una moneda sin precio se ve vacía a propósito.
 *
 * El formato sale de `formatPrice` del contrato, con el locale del
 * idioma activo: los mismos centavos se leen "1,299.00" en inglés y
 * "1,299.00" en español, pero el día que cambie la convención cambia
 * para toda la app a la vez.
 */
export function ProductPrices({ prices, compareAtPrices }: ProductPricesProps) {
  const { i18n } = useTranslation();
  const locale = i18n.language.startsWith("en") ? "en-US" : "es-MX";

  const withPrice = CURRENCIES.filter((currency) => prices[currency] !== undefined);
  if (withPrice.length === 0) {
    return <span className="text-ds-ink-3">—</span>;
  }

  return (
    <div className="flex flex-col items-end gap-0.5">
      {withPrice.map((currency) => {
        const cents = prices[currency] as number;
        const compareAt = compareAtPrices?.[currency];
        return (
          <span key={currency} className="flex items-baseline justify-end gap-1.5">
            {compareAt !== undefined && (
              <s className="text-[11px] text-ds-ink-3">
                {formatPrice(compareAt, currency, locale)}
              </s>
            )}
            {/*
              No se añade la etiqueta "MXN"/"USD" al lado: `formatPrice`
              ya distingue las monedas según el idioma —en español, la
              local sale como "$1,299.50" y la extranjera como
              "USD 65.00"— y ponerla otra vez se leería "USD 65.00 USD".
            */}
            <span className="font-semibold tabular-nums text-ds-ink">
              {formatPrice(cents, currency, locale)}
            </span>
          </span>
        );
      })}
    </div>
  );
}
